// src/pages/UserListPage.tsx
import ActiveToggleButton from "@/components/ActiveToggleButton";
import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import { getTenantId } from "@/lib/storeManager";
import SocialConfigurationService from "@/services/APIs/SocialConfigurationService";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { SocialConfiguration } from "@/utils/types/socialConfiguration";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { t } from "i18next";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const SocialConfigurationListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantId = getTenantId() ?? "";
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const {
    data,
    isLoading,
    refetch: refetchSocialConfigurations,
  } = useQuery({
    queryKey: ["socialConfigurations", tenantId, page, pageSize],
    queryFn: () =>
      SocialConfigurationService.listSocialConfigurations(page, pageSize),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.SOCIAL_CONFIGURATION_EDIT(id));
  };

  const handleDelete = async (id: string) => {
    await SocialConfigurationService.deleteSocialConfiguration(id);
    queryClient.invalidateQueries({ queryKey: ["socialConfigurations"] });
    refetchSocialConfigurations();
  };

  const columns: ProColumns<SocialConfiguration>[] = [
    {
      title: "Social Configuration Provider",
      render: (_, record) => `${record.provider}`,
      sorter: (a, b) => a.provider!.localeCompare(b.provider!),
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.socialConfigurationTable}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEdit(record.id)}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit Social Configuration"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this social configuration?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Social Configuration"
          />
        </Popconfirm>,
      ],
    },
  ];

  const handleCreate = () => {
    navigate(navigationRoutes.SOCIAL_CONFIGURATION_ADD);
  };

  const toolBarRender = () => {
    return [
      <CustomButton
        key="add"
        type="primary"
        onClick={handleCreate}
        title="Add Social Configuration"
      >
        <Plus size={16} className="mr-1" /> {t("Add Social Configurations")}
      </CustomButton>,
    ];
  };

  return (
    <BaseTable<SocialConfiguration>
      loading={isLoading}
      columns={columns}
      dataSource={data?.data ?? []}
      page={page}
      pageSize={pageSize}
      total={data?.data?.length ?? 0}
      onPageChange={(page, pageSize) => {
        setPage(page);
        setPageSize(pageSize ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", pageSize.toString());
        navigate(
          `${navigationRoutes.SOCIAL_CONFIGURATION}?${searchParams.toString()}`
        );
        refetchSocialConfigurations();
      }}
      rowKey="id"
      headerTitle="Social Configurations List"
      toolBarRender={toolBarRender}
      request={() => refetchSocialConfigurations()}
      tableName={gridName.socialConfigurationTable}
    />
  );
};

export default SocialConfigurationListPage;
